/**
 * Wording rules for the Maintenance page (AB#6872).
 *
 * PURE module: no React, MUI, axios or auth imports, so it is unit-testable.
 */

/**
 * Plain-language labels for the outcome tokens jobs write to their report rows. The tokens
 * themselves stay in the downloaded CSV and in the cell's tooltip, so a row can still be
 * matched to the stored audit.
 */
export const OUTCOME_LABELS: Record<string, string> = {
  Changed: "Changed",
  AlreadyCorrect: "Already correct",
  NoDistributionDate: "No distribution date",
  PaidBeforeDistribution: "Paid before distribution",
  NotACandidate: "Not a candidate",
  NotPaidAch: "Not a paid ACH bill",
  Failed: "Failed",
  NotFound: "Not found",
  NotMoved: "Not moved",
  NotCorrectable: "Can't be corrected",
  NoFacilityOwner: "No facility owner",
  UnrecognizedClassification: "Unrecognized classification",
  NotEmpty: "Payee still has bills",
  AlreadyGone: "Already deleted",
  KeptMoveFailed: "Kept: bill move failed",
  AlreadyMoved: "Already moved",
  NotPaid: "Not paid",
  NotTaxYear2026: "Not tax year 2026",
  Ambiguous: "Ambiguous ref code",
  TargetMissing: "Target payee missing",
};

/**
 * "SomeNewToken2026" -> "Some new token 2026". Used for a token a job adds before this map
 * learns it, so a new outcome still reads as words rather than as code.
 */
export function humanizeToken(token: string): string {
  const words = token
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([A-Za-z])(\d)/g, "$1 $2")
    .replace(/(\d)([A-Za-z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  if (!words) return "";
  return (
    words.charAt(0).toUpperCase() +
    words
      .slice(1)
      .split(" ")
      .map((w) => (/^[A-Z0-9]+$/.test(w) && w.length > 1 ? w : w.toLowerCase()))
      .join(" ")
  );
}

/** The readable label for a report row's outcome token. Empty or missing -> "—". */
export function outcomeLabel(token?: string | null): string {
  const t = (token ?? "").trim();
  if (!t) return "—";
  return OUTCOME_LABELS[t] ?? humanizeToken(t);
}

/** The parts of a run the apply dialog's title depends on. */
export interface ApplyTitleRun {
  state: string;
  dryRun: boolean;
  candidatesAtStart: number;
}

/**
 * Title of the "Run and apply" confirmation. It names a count only when the job's current
 * run is a completed dry run, because that is the only time the page knows what the apply
 * would touch; otherwise it names the job.
 */
export function applyConfirmTitle(
  job: { title: string; unitNoun: string | null },
  current: ApplyTitleRun | null | undefined
): string {
  if (current && current.dryRun && current.state === "Completed") {
    const unit = job.unitNoun || "payees";
    const n = (current.candidatesAtStart ?? 0).toLocaleString("en-US");
    return `Apply changes to ${n} ${unit}?`;
  }
  return `Apply ${job.title}?`;
}

/** Body of the apply confirmation when the job does not supply its own wording. */
export const GENERIC_APPLY_CONFIRMATION =
  "This writes to the database in this environment. It runs in the background and can be cancelled at a page boundary; it is safe to run again. Run a dry run first if you have not.";

/** The page shows at most this many errored bills per run. */
export const FAILURES_SHOWN_CAP = 500;

/** Text of the shortcut to the Errored bills table, e.g. "2 failed bills", "500+ failed bills". */
export function failedBillsLabel(count: number): string {
  const n = Math.max(0, count);
  const shown = `${n.toLocaleString("en-US")}${
    n >= FAILURES_SHOWN_CAP ? "+" : ""
  }`;
  return `${shown} failed ${n === 1 ? "bill" : "bills"}`;
}

/**
 * Colour of a stat tile: a Failed tile with anything in it is an error, a tile the job
 * highlights is a warning, the rest are plain.
 */
export function statTone(
  label: string,
  value: number,
  highlight?: boolean
): "err" | "warn" | undefined {
  if (/^\s*failed\b/i.test(label) && value > 0) return "err";
  if (highlight) return "warn";
  return undefined;
}

/** One date's heading on the page and the jobs created or last changed that day, newest first. */
export interface JobDateGroup<T> {
  /** Local calendar date, "2026-10-21"; "none" for jobs with no date. */
  key: string;
  label: string;
  jobs: T[];
}

/** Heading of the group for jobs that do not say when they were last changed. */
export const NO_DATE_LABEL = "Date not recorded";

const timeOf = (iso?: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

const localDateKey = (t: number): string => {
  const d = new Date(t);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

/**
 * The page's job list: grouped by the local date each job was created or last changed, newest
 * date first, and newest first within a date (title breaks a tie). Jobs with no usable date
 * come last, under their own heading, so a job is never dropped from the page.
 */
export function groupJobsByLastModified<
  T extends { title: string; lastModified?: string | null }
>(jobs: T[]): JobDateGroup<T>[] {
  const dated = jobs
    .map((job) => ({ job, t: timeOf(job.lastModified) }))
    .sort((a, b) => {
      if (a.t === null || b.t === null) {
        if (a.t !== b.t) return a.t === null ? 1 : -1;
      } else if (a.t !== b.t) {
        return b.t - a.t;
      }
      return a.job.title.localeCompare(b.job.title);
    });

  const groups: JobDateGroup<T>[] = [];
  for (const { job, t } of dated) {
    const key = t === null ? "none" : localDateKey(t);
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      group = {
        key,
        label:
          t === null
            ? NO_DATE_LABEL
            : new Date(t).toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              }),
        jobs: [],
      };
      groups.push(group);
    }
    group.jobs.push(job);
  }
  return groups;
}

/** The card's line, e.g. "Last modified Oct 21, 2026, 2:15 PM". Null when the job has no date. */
export function lastModifiedLabel(iso?: string | null): string | null {
  const t = timeOf(iso);
  if (t === null) return null;
  return `Last modified ${new Date(t).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })}`;
}
